import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, QrCode, Clock, CheckCircle2, LogOut, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getParentHeaders = () => {
  const token = localStorage.getItem('parent_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const CheckinPais = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [observacoes, setObservacoes] = useState('');
  const [checkin, setCheckin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [meetingActive, setMeetingActive] = useState(false);
  const [meetingInfo, setMeetingInfo] = useState(null);

  useEffect(() => { fetchChildren(); fetchMeetingStatus(); }, []);

  // Poll meeting status every 15 seconds
  useEffect(() => {
    if (meetingActive) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/checkin/meeting-status`);
        if (res.data.active) {
          setMeetingActive(true);
          setMeetingInfo(res.data.meeting);
          toast.success('Encontro iniciado! Voce ja pode fazer o check-in.');
        }
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [meetingActive]);

  const fetchMeetingStatus = async () => {
    try {
      const res = await axios.get(`${API}/checkin/meeting-status`);
      setMeetingActive(res.data.active);
      setMeetingInfo(res.data.meeting || res.data.next_agenda || null);
    } catch {}
  };

  useEffect(() => { fetchChildren(); }, []);

  // Auto-poll for status updates every 10 seconds when viewing active checkin
  useEffect(() => {
    if (!checkin || ['checked_out'].includes(checkin.status)) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/checkin/my-children`, { headers: getParentHeaders() });
        const updated = (res.data || []);
        setChildren(updated);
        if (selectedChild) {
          const found = updated.find(c => c.id === selectedChild.id);
          if (found?.checkin) {
            setCheckin(found.checkin);
            // Notify on status change
            if (found.checkin.status !== checkin.status) {
              if (found.checkin.status === 'checked_in') {
                toast.success(`${found.nome} esta no Raizes!`);
              } else if (found.checkin.status === 'checked_out') {
                toast.success(`${found.nome} foi retirado(a) por ${found.checkin.checkout_by || 'voluntario'} as ${found.checkin.checkout_time ? new Date(found.checkin.checkout_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}`);
              }
            }
          }
        }
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [checkin?.status, selectedChild?.id]);

  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API}/checkin/my-children`, { headers: getParentHeaders() });
      setChildren(res.data || []);
      // If any child has active checkin, show it
      const active = (res.data || []).find(c => c.checkin && ['pending', 'checked_in', 'checkout_requested'].includes(c.checkin.status));
      if (active) {
        setSelectedChild(active);
        setCheckin(active.checkin);
      }
    } catch (e) { toast.error('Erro ao carregar dados'); }
    setLoading(false);
  };

  const handleGenerate = async (child) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/checkin/generate`, {
        child_id: child.id,
        observacoes
      }, { headers: getParentHeaders() });
      setCheckin(res.data);
      setSelectedChild(child);
      toast.success('QR Code gerado!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar QR'); }
    setGenerating(false);
  };

  const handleRequestCheckout = async () => {
    if (!checkin) return;
    try {
      await axios.post(`${API}/checkin/request-checkout`, {
        checkin_id: checkin.id
      }, { headers: getParentHeaders() });
      toast.success('Checkout solicitado!');
      fetchChildren();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const statusLabel = (s) => {
    if (s === 'pending') return { text: 'Aguardando entrada', color: 'bg-amber-100 text-amber-700', icon: Clock };
    if (s === 'checked_in') return { text: 'Presente', color: 'bg-green-100 text-green-700', icon: CheckCircle2 };
    if (s === 'checkout_requested') return { text: 'Saída solicitada', color: 'bg-blue-100 text-blue-700', icon: LogOut };
    if (s === 'checked_out') return { text: 'Saiu', color: 'bg-gray-100 text-gray-600', icon: CheckCircle2 };
    return { text: s, color: 'bg-gray-100 text-gray-600', icon: AlertCircle };
  };

  if (loading) return <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" /></div>;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-8">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <QrCode className="w-7 h-7" />
            <h1 className="font-nunito font-bold text-3xl">Check-in</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">Gere o QR Code para a entrada do seu filho</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Meeting status banner */}
        {!meetingActive && !checkin && (
          <Card className="bg-amber-50 rounded-xl p-4 border border-amber-300 text-center" data-testid="no-meeting-banner">
            <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="font-nunito font-bold text-amber-800">Nenhum encontro ativo</p>
            <p className="font-inter text-xs text-amber-600 mt-1">Aguarde o time iniciar o encontro para gerar o QR Code</p>
            {meetingInfo && meetingInfo.data && !meetingInfo.started_at && (
              <p className="font-inter text-xs text-gray-500 mt-2">
                Proximo: {new Date(meetingInfo.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' })} as {meetingInfo.horario_inicio || '16:30'}
              </p>
            )}
          </Card>
        )}

        {meetingActive && !checkin && meetingInfo && (
          <Card className="bg-green-50 rounded-xl p-3 border border-green-300 flex items-center gap-3" data-testid="meeting-active-banner">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
            <div>
              <p className="font-nunito font-bold text-sm text-green-800">{meetingInfo.title}</p>
              <p className="font-inter text-[10px] text-green-600">Encontro em andamento</p>
            </div>
          </Card>
        )}

        {/* Active checkin view */}
        {checkin && selectedChild ? (
          <div className="space-y-4">
            <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5] text-center">
              <h3 className="font-nunito font-bold text-xl text-[#1A4D2E] mb-1">{checkin.child_name}</h3>
              {checkin.child_codigo && <p className="font-mono text-xs text-gray-400 mb-3">{checkin.child_codigo}</p>}

              {(() => { const st = statusLabel(checkin.status); return (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${st.color} mb-4`}>
                  <st.icon className="w-4 h-4" /> {st.text}
                </span>
              ); })()}

              {/* QR Code */}
              <div className="bg-white border-2 border-dashed border-[#1A4D2E]/20 rounded-2xl p-6 inline-block mx-auto">
                <QRCodeSVG value={checkin.qr_code} size={200} level="H" />
              </div>

              {/* Token */}
              <div className="mt-4 bg-[#F2F7F4] rounded-xl p-4">
                <p className="font-inter text-xs text-gray-500 mb-1">Token de Segurança</p>
                <p className="font-nunito font-bold text-4xl text-[#1A4D2E] tracking-[0.3em]">{checkin.token}</p>
              </div>

              {checkin.observacoes && (
                <div className="mt-3 bg-amber-50 rounded-xl p-3 text-left">
                  <p className="font-inter text-xs text-amber-700 font-semibold mb-1">Observações:</p>
                  <p className="font-inter text-sm text-amber-800">{checkin.observacoes}</p>
                </div>
              )}

              {checkin.checkin_time && (
                <p className="font-inter text-xs text-gray-400 mt-3">
                  Entrada: {new Date(checkin.checkin_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  {checkin.pontualidade && <span className="ml-2 text-green-600 font-semibold">Pontual</span>}
                </p>
              )}
            </Card>

            {/* Checkout button */}
            {checkin.status === 'checked_in' && (
              <button onClick={handleRequestCheckout} data-testid="request-checkout-btn"
                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl font-nunito font-bold text-lg transition-all flex items-center justify-center gap-2">
                <LogOut className="w-5 h-5" /> Solicitar Saída
              </button>
            )}

            {checkin.status === 'checked_out' && (
              <Card className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
                <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-nunito font-bold text-green-800">Checkout realizado!</p>
                {checkin.checkout_time && (
                  <p className="font-inter text-xs text-green-600 mt-1">
                    Saída: {new Date(checkin.checkout_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    {checkin.checkout_by && ` - por ${checkin.checkout_by}`}
                  </p>
                )}
              </Card>
            )}

            <button onClick={() => { setCheckin(null); setSelectedChild(null); fetchChildren(); }}
              className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm font-inter">
              Voltar para lista de filhos
            </button>
          </div>
        ) : (
          /* Child selection */
          <>
            {children.length === 0 ? (
              <Card className="bg-white rounded-2xl p-12 text-center shadow-md">
                <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-inter text-gray-500">Nenhum filho cadastrado</p>
              </Card>
            ) : (
              children.map(child => {
                const ci = child.checkin;
                const hasActive = ci && ['pending', 'checked_in', 'checkout_requested'].includes(ci.status);
                return (
                  <Card key={child.id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]" data-testid={`child-checkin-${child.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-nunito font-bold text-lg text-[#1A4D2E]">{child.nome}</h4>
                        {child.codigo && <p className="font-mono text-xs text-gray-400">{child.codigo}</p>}
                      </div>
                      {hasActive ? (
                        <button onClick={() => { setSelectedChild(child); setCheckin(ci); }}
                          className="bg-[#1A4D2E] text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5">
                          <QrCode className="w-4 h-4" /> Ver QR
                        </button>
                      ) : ci?.status === 'checked_out' ? (
                        <span className="text-xs text-gray-400 font-semibold">Saiu hoje</span>
                      ) : meetingActive ? (
                        <button onClick={() => handleGenerate(child)} disabled={generating}
                          data-testid={`generate-qr-${child.id}`}
                          className="bg-[#1A4D2E] hover:bg-[#163f25] text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
                          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />} Gerar QR
                        </button>
                      ) : (
                        <span className="text-xs text-amber-500 font-semibold">Aguardando</span>
                      )}
                    </div>
                  </Card>
                );
              })
            )}

            {/* Observations field */}
            {children.some(c => !c.checkin || c.checkin.status === 'checked_out') && (
              <div className="mt-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Observações (opcional)</label>
                <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none resize-none"
                  placeholder="Alergias, medicamentos, recados para os voluntários..."
                  rows={3} data-testid="observacoes-input" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CheckinPais;
